import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Custom hook that encapsulates all business logic for SizeButtons component
 * @param {Object} props - Component props
 * @returns {Object} - Processed data and handlers
 */
export const useSizeButtons = ({
  product,
  appCtx,
  labels,
  sizes,
  selectedSize,
  onSizeChange,
  digitalData,
  sizeChartData,
  fomo,
  inDeliveryOptions,
}) => {
  const [isDrawerActive, setIsDrawerActive] = useState(false);
  const [isSizeChartLoading, setSizeChartLoaderVisibility] = useState(true);
  const displaySizeChartBtnRef = useRef();

  const processedData = useMemo(() => {
    const sizeChart = product?.attributes?.sizeChart ?? {};
    const { sizeChartName, sizeChartUrl } = sizeChart;
    const regionCode = appCtx?.regionCode ?? '';
    const currentVariant = product?.currentVariant ?? '';

    // Configuration flags with optional chaining
    const guideSymbol = product?.guideSymbol ?? false;
    const isEnhancedSizeSelectorActive = appCtx?.siteConfig?.toggles?.isEnhancedSizeSelectorActive ?? true;
    const isSizeChartDataVisible = appCtx?.siteConfig?.toggles?.isSizeChartDataVisible ?? true;
    const isFomoBadgeVisible = appCtx?.siteConfig?.toggles?.isFomoBadgeVisible ?? true;
    const fomoMessage = appCtx?.siteConfig?.textDictionary?.FOMO_MESSAGE ?? 'Últimas unidades';
    const isModernLineContainerEnabled = appCtx?.siteConfig?.toggles?.isModernLineContainerEnabled;
    const isActiveTabAssistentePersonal = appCtx?.siteConfig?.toggles?.isActiveTabAssistentePersonal ?? false;

    // Enhanced size selector className
    const enhancedSizeSelectorClassName = isEnhancedSizeSelectorActive ? 'enhanced-size-selector' : '';

    // Size drawer title with fallback
    const sizeDrawerTitle = labels?.FIND_YOUR_SIZE ?? 'ENCUENTRA TU TALLAS';

    // Tab headings configuration
    const tabHeadings = {
      0: labels?.PERSONAL_ASSISTANT ?? 'Asistente personal',
      1: labels?.SIZE_GUIDE ?? 'Guía de tallas',
    };

    const formattedSizeChartData = sizeChartData?.sizeChart
      ? Object.entries(sizeChartData.sizeChart)
          .map(([key, value]) => (key === 'cm' ? `${value} centímetros` : `${key} ${value}`))
          .join(' - ')
      : '';

    // Fomo badge enabled state
    const isFomoBadgeEnabled = isEnhancedSizeSelectorActive && isFomoBadgeVisible && selectedSize && fomo?.enabled;

    return {
      sizeChart,
      sizeChartName,
      sizeChartUrl,
      regionCode,
      currentVariant,
      guideSymbol,
      isEnhancedSizeSelectorActive,
      isSizeChartDataVisible,
      isFomoBadgeVisible,
      fomoMessage,
      isModernLineContainerEnabled,
      isActiveTabAssistentePersonal,
      enhancedSizeSelectorClassName,
      sizeDrawerTitle,
      tabHeadings,
      formattedSizeChartData,
      isFomoBadgeEnabled,
    };
  }, [product, appCtx, labels, selectedSize, sizeChartData, fomo]);

  const sortedSizes = useMemo(() => {
    if (!sizes || !Array.isArray(sizes)) return [];

    return [...sizes].sort((a, b) => {
      const valueA = a.value?.split(' ');
      const valueB = b.value?.split(' ');
      if (valueA && valueB && valueA.length > 1 && valueB.length > 1) {
        return Number(valueA[0]) - Number(valueB[0]);
      }
      return 0;
    });
  }, [sizes]);

  const activateDrawer = useCallback((isActive) => {
    setIsDrawerActive(isActive);
  }, []);

  const handleRenderSizeButton = useCallback(
    ({ value, available, index }) => {
      return {
        value,
        available,
        index,
        selectedSize,
        onSizeChange,
        enhancedSizeSelectorClassName: processedData.enhancedSizeSelectorClassName,
        isModernLineEnabled: processedData.isModernLineContainerEnabled,
      };
    },
    [
      selectedSize,
      onSizeChange,
      processedData.enhancedSizeSelectorClassName,
      processedData.isModernLineContainerEnabled,
    ]
  );

  const handleDrawerOpen = useCallback(() => {
    if (processedData.isActiveTabAssistentePersonal) {
      setSizeChartLoaderVisibility(false);
    } else {
      setSizeChartLoaderVisibility(true);
    }
  }, [processedData.isActiveTabAssistentePersonal]);

  const handleSizeChartLoad = useCallback(() => {
    setSizeChartLoaderVisibility(false);
  }, []);

  const handleOpenSizeChart = useCallback(() => {
    setSizeChartLoaderVisibility(true);
  }, []);

  useEffect(() => {
    if (!displaySizeChartBtnRef.current || !digitalData?.pdp?.setProductSizeTools) {
      return;
    }

    const sizeChartBtnInnerText = (displaySizeChartBtnRef.current.innerText || '').toLowerCase();
    const sizeChartLabel = labels?.SIZE_CHART?.toLowerCase() ?? '';

    if (sizeChartBtnInnerText.includes(sizeChartLabel)) {
      digitalData.pdp.setProductSizeTools('1');
    } else if (sizeChartBtnInnerText.includes('encuentra tu talla')) {
      digitalData.pdp.setProductSizeTools('2');
    }
  }, [displaySizeChartBtnRef, digitalData, labels]);

  useEffect(() => {
    const dispatchCustomEvent = (eventName, payload) => {
      const event = new CustomEvent(eventName, {
        bubbles: true,
        detail: { payload },
      });
      window.dispatchEvent(event);
    };

    const { currentVariant, formattedSizeChartData, isFomoBadgeEnabled, fomoMessage } = processedData;

    if (currentVariant && formattedSizeChartData) {
      dispatchCustomEvent('DDPDPShowSizeChartData', `${currentVariant}|${formattedSizeChartData}`);
    } else {
      dispatchCustomEvent('DDPDPShowSizeChartData', '');
    }

    if (currentVariant && isFomoBadgeEnabled) {
      dispatchCustomEvent('DDPDPShowFomoMessage', `${currentVariant}|${fomoMessage}`);
    } else {
      dispatchCustomEvent('DDPDPShowFomoMessage', '');
    }
  }, [processedData]);

  const computedValues = useMemo(
    () => ({
      shouldRender: Array.isArray(sizes) && sizes.length > 0,
      hasDrawer: Boolean(processedData.sizeChartName && processedData.sizeChartUrl),
      containerClassName: `size-container fa--sizes rebranded ${processedData.enhancedSizeSelectorClassName} ${
        inDeliveryOptions ? 'in-delivery-options' : ''
      }`.trim(),
      titleContainerClassName: `title-container ${processedData.enhancedSizeSelectorClassName}`.trim(),
      sizeTitleClassName: `size-title ${processedData.enhancedSizeSelectorClassName}`.trim(),
      sizeOptionsClassName: `size-options ${processedData.enhancedSizeSelectorClassName}`.trim(),
    }),
    [sizes, processedData, inDeliveryOptions]
  );

  return {
    // Processed data with spread
    ...processedData,

    // State values
    isDrawerActive,
    isSizeChartLoading,
    displaySizeChartBtnRef,

    // Processed arrays
    sortedSizes,

    // Event handlers with useCallback
    activateDrawer,
    handleRenderSizeButton,
    handleDrawerOpen,
    handleSizeChartLoad,
    handleOpenSizeChart,
    setSizeChartLoaderVisibility,

    // Computed values with spread
    ...computedValues,
  };
};
